/**
 * „Mein Konto" — alles, was dem angemeldeten Menschen gehoert, an einer
 * Stelle: Profil, Sicherheit, Benachrichtigungen.
 *
 * Der aktive Reiter steht in der URL (`/account?tab=security`) und nicht im
 * State: so ist er verlinkbar, ueberlebt einen Reload, und die alte Route
 * `/security` kann einfach dorthin zeigen (siehe `App.tsx`).
 *
 * Eine Sitzungsverwaltung („aktive Geraete") gibt es bewusst nicht — der
 * Passwortwechsel beendet ohnehin alle anderen Sitzungen, und mehr Knoepfe
 * haetten hier niemandem geholfen.
 */
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { AccountNotifications } from '@/components/AccountNotifications';
import { AccountProfile } from '@/components/AccountProfile';
import { AccountSecurity } from '@/components/AccountSecurity';
import { TabList, TabPanel, type TabItem } from '@/components/ui/tabs';

const TABS = ['profile', 'security', 'notifications'] as const;
type Tab = (typeof TABS)[number];

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function AccountPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('tab');
  const active: Tab = isTab(raw) ? raw : 'profile';

  const items: TabItem[] = [
    { value: 'profile', label: t('account.tab_profile') },
    { value: 'security', label: t('account.tab_security') },
    { value: 'notifications', label: t('account.tab_notifications') },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('account.title')}</h1>

      <TabList
        items={items}
        value={active}
        label={t('account.tabs_label')}
        // `replace`: die Reiter sollen den Zurueck-Knopf nicht vollmuellen.
        onChange={(next) => setSearchParams({ tab: next }, { replace: true })}
      />

      <TabPanel value={active}>
        {active === 'profile' ? <AccountProfile /> : null}
        {active === 'security' ? <AccountSecurity /> : null}
        {active === 'notifications' ? <AccountNotifications /> : null}
      </TabPanel>
    </div>
  );
}
